import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { initVersionedSchema, MixedSchema } from '@principle-theorem/editor';
import {
  createInsertTemplateMenuButton,
  EditorPresetsService,
  PrincipleEditorModule,
} from '@principle-theorem/ng-interactions';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import { createInsertPatientDocumentMenuButton } from '@principle-theorem/ng-patient';
import {
  AlertComponent,
  NG_SHARED_CONFIG,
  NgDialogModule,
  NgFireMediaUploader,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  IPatient,
  IPractice,
  IReferral,
  ReferralActor,
} from '@principle-theorem/principle-core/interfaces';
import { filterUndefined, WithRef } from '@principle-theorem/shared';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { Storage } from '@angular/fire/storage';
import { MenuButtonLoaderFn } from '@principle-theorem/ng-prosemirror';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { buildReferralTemplatesWithContext } from '../../helpers/build-referral-templates-with-context';

export interface ISendPatientLetterDialogData {
  patient: WithRef<IPatient>;
  practice: WithRef<IPractice>;
  currentUser: ReferralActor;
  referral: WithRef<IReferral>;
  defaultSubject: string;
  defaultEmailBody: MixedSchema;
}

export interface ISendPatientLetterDialogResult {
  subject: string;
  emailBody: MixedSchema;
}

interface ISendPatientLetterForm {
  subject: string;
  emailBody: MixedSchema;
}

@Component({
  selector: 'pr-send-patient-letter-dialog',
  imports: [
    NgDialogModule,
    MatFormFieldModule,
    AlertComponent,
    PrincipleEditorModule,
    InformationBoxComponent,
  ],
  templateUrl: './send-patient-letter-dialog.component.html',
  styleUrl: './send-patient-letter-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendPatientLetterDialogComponent {
  private _editorPresets = inject(EditorPresetsService);
  private _organisation = inject(OrganisationService);
  private _storage = inject(Storage);
  private _sharedConfig = inject(NG_SHARED_CONFIG);

  dialogRef = inject(MatDialogRef<SendPatientLetterDialogComponent>);
  data = inject<ISendPatientLetterDialogData>(MAT_DIALOG_DATA);
  menuItems: MenuButtonLoaderFn[];

  form = new TypedFormGroup<ISendPatientLetterForm>({
    subject: new TypedFormControl<string>('', Validators.required),
    emailBody: new TypedFormControl<MixedSchema>(initVersionedSchema()),
  });

  formValid = toSignal(
    this.form.statusChanges.pipe(map(() => this.form.valid)),
    { initialValue: this.form.valid }
  );

  hasPatientEmail = computed(() => {
    const email = this.data.patient.email;
    return !!email && email.trim().length > 0;
  });

  patientEmail = computed(() => {
    return this.data.patient.email ?? 'No email configured';
  });

  canSend = computed(() => {
    return this.hasPatientEmail() && this.formValid();
  });

  constructor() {
    this._initialiseForm();

    const uploader = new NgFireMediaUploader(
      this._storage,
      this._organisation.storagePath$.pipe(filterUndefined())
    );

    const menuCoordinator$ = new Subject<string>();
    this.menuItems = this._editorPresets.defaultMenuItems(uploader, [
      createInsertTemplateMenuButton({
        templates$: buildReferralTemplatesWithContext(
          this.data.referral,
          this.data.practice,
          this._sharedConfig.appUrl,
          this._organisation.brand$.pipe(filterUndefined())
        ),
        menuCoordinator$,
      }),
      createInsertPatientDocumentMenuButton({
        patient: this.data.patient,
        menuCoordinator$,
      }),
    ]);
  }

  send(): void {
    if (!this.canSend()) {
      return;
    }

    const data = this.form.getRawValue();

    const result: ISendPatientLetterDialogResult = {
      subject: data.subject ?? '',
      emailBody: data.emailBody ?? initVersionedSchema(),
    };

    this.dialogRef.close(result);
  }

  private _initialiseForm(): void {
    this.form.controls.subject.setValue(this.data.defaultSubject);
    this.form.controls.emailBody.setValue(this.data.defaultEmailBody);
  }
}
